import { Api, RequestParams } from "./trex-anchor-api";
import type { TrexApiProxy } from "../trex-api/trex-api-proxy";

export class TrexAnchorApiProxy {
  private client: InstanceType<typeof Api>;
  public token: string | null = null;
  public refreshToken: string | null = null;
  private isRefreshing = false;
  private refreshPromise: Promise<void> | null = null;
  private trexProxy: TrexApiProxy | null = null;

  private chain_id = "";

  constructor(
    client: InstanceType<typeof Api>,
    chain_id: string,
    baseUrl?: string,
    trexProxy?: TrexApiProxy,
  ) {
    this.client = client;
    if (baseUrl) {
      this.client.baseUrl = baseUrl;
    }
    this.trexProxy = trexProxy || null;
    this.chain_id = chain_id || "";
  }

  getHeaders(params: any) {
    return {
      ...params.headers,
      client_id: "t-rex",
      chain_id: this.chain_id,
      jwt_token: this.token,
    };
  }
  getV2Headers(params: any) {
    return {
      ...params.headers,
      client_id: "t-rex",
      chain_id: this.chain_id,
      jwt_token: this.token,
      "X-Project-Id": "t-rex",
      "X-Chain-Id": this.chain_id,
      Authorization: this.token,
    };
  }

  /** Updates `chain_id` / `X-Chain-Id` headers for subsequent Anchor API calls. */
  setChainId(chainId: string) {
    this.chain_id = chainId?.trim() || "";
  }

  setToken(token: string) {
    this.token = token;
  }

  setRefreshToken(refreshToken: string) {
    this.refreshToken = refreshToken;
  }

  clearToken() {
    this.token = null;
    this.refreshToken = null;
  }

  // 刷新token逻辑与PortalTrexApiProxy一致，但调用PortalTrexApi的refresh接口
  private async refreshAccessToken(): Promise<void> {
    if (this.isRefreshing) {
      return this.refreshPromise!;
    }
    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      try {
        if (!this.trexProxy)
          throw new Error("No PortalTrexApiProxy instance for token refresh");
        await this.trexProxy.refreshAccessToken();
        const newToken = this.trexProxy.token || "";
        const newRefreshToken = this.trexProxy.refreshToken || "";
        this.setToken(newToken);
        this.setRefreshToken(newRefreshToken);
      } catch (error) {
        this.clearToken();
        throw error;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();
    return this.refreshPromise;
  }

  private async execute<T>(
    request: () => Promise<T>,
    requireInitialToken: boolean = true,
  ): Promise<T> {
    if (requireInitialToken && !this.token) {
      throw new Error("No token available");
    }

    try {
      const res = await request();
      if ((res as any).data?.code === "9101") {
        await this.refreshAccessToken();
        // 重新执行请求
        return await request();
      }
      return res;
    } catch (error: any) {
      if (error?.status === 401 && this.refreshToken) {
        await this.refreshAccessToken();
        return request();
      }
      throw error;
    }
  }

  private async requestWithAuth<T>(request: () => Promise<T>): Promise<T> {
    return this.execute(request, true);
  }

  private async requestWithoutAuth<T>(request: () => Promise<T>): Promise<T> {
    return this.execute(request, false);
  }

  get v1() {
    return {
      ...this.client.v1,
      getNftSeriesById: (
        type: string,
        series: string,
        query?: { customerId?: string },
        params: RequestParams = {},
      ) =>
        this.requestWithAuth(() =>
          this.client.v1.getNftSeriesById(type, series, query, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      getNftSeries: (type: string) =>
        this.requestWithAuth(() =>
          this.client.v1.nftSeries(type, { headers: this.getHeaders({}) }),
        ),
      checkGet: () =>
        this.requestWithAuth(() =>
          this.client.v1.getCheckResult("badge", {
            headers: this.getHeaders({}),
          }),
        ),
      checkPost: () =>
        this.requestWithAuth(() =>
          this.client.v1.check("badge", { headers: this.getHeaders({}) }),
        ),
      getBadgeByType: (type: string) =>
        this.requestWithAuth(() =>
          this.client.v1.getNftSeriesById(
            "badge",
            type,
            {},
            { headers: this.getHeaders({}) },
          ),
        ),
      getBadgeAssets: (query?: {
        customerId?: string;
        status?: string;
        strategy?: string;
        series?: string;
      }) =>
        this.requestWithAuth(() =>
          this.client.v1.badgeAssets(query, { headers: this.getHeaders({}) }),
        ),
      // 团队相关API
      getTeamLeaderboard: (query?: {
        page?: number;
        size?: number;
        type?: string;
        joinable?: boolean;
      }) =>
        this.requestWithoutAuth(() =>
          this.client.v1.getTeamLeaderboard(query, {
            headers: this.getV2Headers({}),
          }),
        ),
      getTeamMemberRanking: (
        teamId: string,
        query?: {
          page?: number;
          size?: number;
          type?: string;
        },
      ) =>
        this.requestWithoutAuth(() =>
          this.client.v1.getTeamMemberRanking(teamId, query, {
            headers: this.getV2Headers({}),
          }),
        ),
      getBadgeContributors: (badgeId: string) =>
        this.requestWithAuth(() =>
          this.client.v1.getBadgeContributors(badgeId, {
            headers: this.getV2Headers({}),
          }),
        ),
      getTeamBadges: (query?: { category?: string; status?: string }) =>
        this.requestWithAuth(() =>
          this.client.v1.getTeamBadges(query, {
            headers: this.getV2Headers({}),
          }),
        ),
      getMyTeamProfile: () =>
        this.requestWithAuth(() =>
          this.client.v1.getMyTeamProfile({
            headers: this.getV2Headers({}),
          }),
        ),
      getNotifications: (query?: {
        teamId?: string;
        page?: number;
        size?: number;
      }) =>
        this.requestWithAuth(() =>
          this.client.v1.getNotifications(query, {
            headers: this.getV2Headers({}),
          }),
        ),
      getTeamDetail: (teamId: string) =>
        this.requestWithoutAuth(() =>
          this.client.v1.getTeamDetail(teamId, {
            headers: this.getV2Headers({}),
          }),
        ),
      markNotificationRead: (notificationId: string) =>
        this.requestWithAuth(() =>
          this.client.v1.markNotificationRead(notificationId, {
            headers: this.getV2Headers({}),
          }),
        ),
      getUserApplications: (query?: {
        page?: number;
        size?: number;
        status?: string;
      }) =>
        this.requestWithAuth(() =>
          this.client.v1.getUserApplications(query, {
            headers: this.getV2Headers({}),
          }),
        ),
      // 申请相关API
      acknowledgeApplication: (applicationId: string) =>
        this.requestWithAuth(() =>
          this.client.v1.acknowledgeApplication(applicationId, {
            headers: this.getV2Headers({}),
          }),
        ),
      approveApplication: (applicationId: string) =>
        this.requestWithAuth(() =>
          this.client.v1.approveApplication(applicationId, {
            headers: this.getV2Headers({}),
          }),
        ),
      denyApplication: (applicationId: string, data: { reason: string }) =>
        this.requestWithAuth(() =>
          this.client.v1.denyApplication(applicationId, data, {
            headers: this.getV2Headers({}),
          }),
        ),
      cancelApplication: (applicationId: string) =>
        this.requestWithAuth(() =>
          this.client.v1.cancelApplication(applicationId, {
            headers: this.getV2Headers({}),
          }),
        ),
      getTeamApplications: (
        teamId: string,
        query?: {
          page?: number;
          size?: number;
          status?: string[];
          sortOrder?: string;
        },
      ) =>
        this.requestWithAuth(() =>
          this.client.v1.getTeamApplications(teamId, query, {
            headers: this.getV2Headers({}),
          }),
        ),
      searchMultiple: (
        query?: {
          /** search keyWords */
          keywords?: string;
          /** zone */
          zone?: string;
          /**
           * nextToken for pagination
           * @format int32
           */
          page?: number;
          /**
           * limit for pagination
           * @format int32
           */
          limit?: number;
          /** searchType List */
          types?: "TEAM"[];
          /** sort List */
          sort?: string[];
        },
        params: RequestParams = {},
      ) =>
        this.requestWithoutAuth(() =>
          this.client.v1.searchMultiple(query, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      transactionHashProcess: (txHash: string, params: RequestParams = {}) =>
        this.requestWithAuth(() =>
          this.client.v1.transactionHashProcess(txHash, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
    };
  }
  get v2() {
    return {
      ...this.client.v2,
      // 这里按需包裹需要token的接口
      getBadgeVerifiers: (platform: string, type: string) =>
        this.requestWithAuth(() =>
          this.client.v2.getBadgeVerifiers(
            { platform, type },
            { headers: this.getV2Headers({}) },
          ),
        ),
      getUserClaimableBadges: (query?: {
        series?: string;
        tokenIds?: string[];
        limit?: number;
        nextToken?: string;
      }) =>
        this.requestWithAuth(() =>
          this.client.v2.getUserClaimableBadges(query, {
            headers: this.getV2Headers({}),
          }),
        ),
      getUserBadgeAssets: (query?: {
        includeClaimable?: boolean;
        strategy?: string;
        series?: string[];
        limit?: number;
        nextToken?: string;
      }) =>
        this.requestWithAuth(() =>
          this.client.v2.getUserBadgeAssets(query, {
            headers: this.getV2Headers({}),
          }),
        ),
      getBadgeSeries: (query?: {
        includeBadges?: boolean;
        status?: string;
        businessType?: string;
        limit?: number;
      }) =>
        this.requestWithAuth(() =>
          this.client.v2.getBadgeSeries(query, {
            headers: this.getV2Headers({}),
          }),
        ),
      getBadgeDetail: (badgeId: string) =>
        this.requestWithAuth(() =>
          this.client.v2.getBadgeDetail(badgeId, {
            headers: this.getV2Headers({}),
          }),
        ),
      anchorUserInfo: (params: RequestParams = {}) =>
        this.requestWithAuth(() =>
          this.client.v2.anchorUserInfo({
            ...params,
            headers: this.getV2Headers(params),
          }),
        ),
    };
  }
  get s1() {
    return {
      ...this.client.s1,
      // 这里按需包裹需要token的接口
    };
  }
  // 其他版本接口可按需添加
}
